import {InterviewSheetAnswer, InterviewSheetQuestion} from "@/medication-checkup/interfaces/interview-sheet";

/**
 * What the questions of the interview sheet ask, read from the type the
 * handler serves: the scene holds its closing action by it, the card draws
 * its rows by it. The handler checks the same on the validation.
 */

/**
 * Whether the question expects a yes or a no.
 */
export const asksYesNo = (question: InterviewSheetQuestion): boolean => {
    return question.type !== 'text' && question.type !== 'choices';
};

/**
 * Whether the question is answered by picking options.
 */
export const takesChoices = (question: InterviewSheetQuestion): boolean => {
    return question.type === 'choices' || question.type === 'yn_choices';
};

/**
 * Whether the answer opens the follow-up of the question: a « yes », or the
 * option it names picked.
 */
export const opensFollowUp = (question: InterviewSheetQuestion, answer: InterviewSheetAnswer | null | undefined): boolean => {
    const followUp = question.follow_up;

    if (!followUp || !answer) {
        return false;
    }

    return followUp.trigger === null
        ? answer.yes_no === true
        : (answer.choices ?? []).includes(followUp.trigger);
};

/**
 * Whether the answer leaves the follow-up unsaid: none of its options picked
 * and nothing typed, or « Autre » picked and left blank.
 */
const followUpUnsaid = (question: InterviewSheetQuestion, answer: InterviewSheetAnswer): boolean => {
    const options = question.follow_up?.options ?? [];
    const other = answer.other ?? null;

    if (other !== null && other.trim() === '') {
        return true;
    }

    return other === null && !(answer.choices ?? []).some(choice => options.includes(choice));
};

/**
 * Whether the question still waits on an answer: its yes/no, an option when
 * it opens on options, and once the follow-up is open, an option of it or
 * the words typed for « Autre ». What is typed is never required otherwise.
 */
export const isUnanswered = (question: InterviewSheetQuestion, answer: InterviewSheetAnswer | null | undefined): boolean => {
    if (asksYesNo(question) && typeof answer?.yes_no !== 'boolean') {
        return true;
    }

    if (question.type === 'choices' && !(answer?.choices ?? []).some(choice => (question.options ?? []).includes(choice))) {
        return true;
    }

    if (!answer || !takesChoices(question) || !opensFollowUp(question, answer)) {
        return false;
    }

    return followUpUnsaid(question, answer);
};
